import React, { useState, useEffect, SyntheticEvent } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import axios from "axios";
import { parseISO, format } from 'date-fns';
import { DateField, type DataTableColumn } from '@routes-plan/ui';
import { Office } from '../../models/office';
import { formatCurrency } from '../../utils/format/currency';
import { minYmd, nowOperationalDateString } from "../../utils/operationalDateTime";
import { useT } from '../../i18n';
import { setPageTitle } from '../../utils/pageTitle';
import { CheckMultiSelect, OfficeSelect, ReportEmpty, ReportFilters, ReportPage, ReportResults, ReportTable } from './components/ReportLayout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface PrincipalExpenseOption {
  id: number;
  name: string;
  typeOperationName: string;
}

interface MainBoxMovementRow {
  id: number;
  date: string;
  expenseName: string;
  description: string;
  typeOperationName: string;
  value: number;
  balanceAfter: number;
}

const CashMovements: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  const t = useT();
  setPageTitle('menu.reports.cash-movements');
  const [queried, setQueried] = useState(false);
  const [office_id, setOffice_id] = useState("");
  const current_date = nowOperationalDateString();
  const [start_date, setStart_date] = useState(current_date);
  const [end_date, setEnd_date] = useState(current_date);
  const [movements, setMovements] = useState<MainBoxMovementRow[]>([]);
  const [principalExpenses, setPrincipalExpenses] = useState<PrincipalExpenseOption[]>([]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>([]);
  const [selectAllExpenses, setSelectAllExpenses] = useState(false);
  const [soloSinCobro, setSoloSinCobro] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ data: PrincipalExpenseOption[] }>("/reports/cash-movements/expense-types");
        setPrincipalExpenses(data.data ?? []);
      } catch {
        setPrincipalExpenses([]);
      }
    })();
  }, []);

  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!office_id || !start_date || !end_date) {
      showAlert(t('cashReports.movements.filtersRequired'), 'warning');
      return;
    }

    const formData = {
      endDate: end_date,
      startDate: start_date,
      officeId: office_id,
      expenseIds: selectedExpenseIds.map((id) => Number(id)),
      onlyWithoutRoute: soloSinCobro,
    };

    showLoading();
    setQueried(true);
    try {
      const { data } = await axios.post<{ data: MainBoxMovementRow[] }>("/reports/cash-movements", formData);
      const list = data.data ?? [];
      setMovements(list);
      if (list.length === 0) {
        showAlert(
          t('cashReports.movements.noResults'),
          'info',
        );
      }
    } catch (error: unknown) {
      console.error("Error:", error);
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        t('cashReports.movements.loadError');
      showAlert(msg, 'error');
      setMovements([]);
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    if (principalExpenses.length === 0) {
      setSelectAllExpenses(false);
      return;
    }
    setSelectAllExpenses(
      selectedExpenseIds.length > 0 && selectedExpenseIds.length === principalExpenses.length,
    );
  }, [selectedExpenseIds, principalExpenses.length]);

  const [offices, setOffices] = useState<Office[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get<{ data: Office[] }>("offices");
        setOffices(data.data ?? []);
      } catch {
        setOffices([]);
      }
    })();
  }, []);

  const formatDateCell = (value: string | undefined) => {
    if (!value) return '-';
    try {
      const s = String(value);
      const iso = s.length <= 10 ? `${s}T12:00:00` : s;
      return format(parseISO(iso), 'yyyy-MM-dd HH:mm:ss');
    } catch {
      return String(value);
    }
  };

  const columns: DataTableColumn<MainBoxMovementRow>[] = [
    { id: 'date', header: t('common.date'), value: (r) => r.date, render: (r) => formatDateCell(r.date), csv: (r) => formatDateCell(r.date), sortable: true },
    { id: 'expenseName', header: t('cashReports.movements.operationDetail'), value: (r) => r.expenseName, sortable: true },
    { id: 'description', header: t('cashReports.description'), value: (r) => r.description, wrap: true, hideBelow: 'sm' },
    { id: 'typeOperationName', header: t('cashReports.movements.operation'), value: (r) => r.typeOperationName, sortable: true },
    { id: 'value', header: t('cashReports.value'), value: (r) => Number(r.value ?? 0), render: (r) => formatCurrency(Number(r.value ?? 0)), align: 'right', sortable: true },
    { id: 'balanceAfter', header: t('cashReports.movements.balanceAfter'), value: (r) => Number(r.balanceAfter ?? 0), render: (r) => formatCurrency(Number(r.balanceAfter ?? 0)), align: 'right' },
  ];

  return (
    <ReportPage
      title={t('cashReports.movements.title')}
      description={
        <>
          {t('cashReports.movements.introStart')}
          <strong>{t('cashReports.movements.introOfficeExpense')}</strong>
          {t('cashReports.movements.introMiddle')}
          <strong>{t('cashReports.movements.introDetail')}</strong>
          {t('cashReports.movements.introEnd')}
        </>
      }
    >
      <ReportFilters onSubmit={() => submit({ preventDefault: () => undefined } as SyntheticEvent)}>
        <DateField
          label={t('cashReports.startDate')}
          value={start_date}
          onChange={(v) => setStart_date(v ?? '')}
          max={minYmd(end_date || nowOperationalDateString(), nowOperationalDateString())}
          size="small"
          fullWidth
        />
        <DateField
          label={t('cashReports.endDate')}
          value={end_date}
          onChange={(v) => setEnd_date(v ?? '')}
          min={start_date}
          max={nowOperationalDateString()}
          size="small"
          fullWidth
        />
        <OfficeSelect offices={offices} value={office_id} onChange={setOffice_id} />
        <CheckMultiSelect
          label={t('cashReports.movements.operationDetail')}
          options={principalExpenses.map((exp) => ({
            id: exp.id,
            name: exp.typeOperationName ? `${exp.name} (${exp.typeOperationName})` : exp.name,
          }))}
          value={selectedExpenseIds}
          onChange={setSelectedExpenseIds}
          allOption={t('cashReports.movements.allDetails')}
          allSelectedText={(count) => t('cashReports.movements.allDetailsCount', { count })}
          someSelectedText={(count) => t('cashReports.movements.detailsSelected', { count })}
        />
        <Tooltip title={t('cashReports.movements.onlyWithoutRouteHint')}>
          <FormControlLabel
            control={<Switch checked={soloSinCobro} onChange={(_, checked) => setSoloSinCobro(checked)} color="primary" />}
            label={t('cashReports.movements.onlyWithoutRoute')}
            sx={{ m: 0, minHeight: 40 }}
          />
        </Tooltip>
      </ReportFilters>

      <ReportResults>
        <ReportTable<MainBoxMovementRow>
          rows={movements}
          columns={columns}
          getRowId={(r) => r.id}
          exportFileName={t('cashReports.movements.exportFileName')}
          ariaLabel={t('cashReports.movements.title')}
          emptyState={
            <ReportEmpty
              queried={queried}
              description={!queried && principalExpenses.length === 0 ? t('cashReports.movements.noCatalog') : undefined}
            />
          }
        />
      </ReportResults>
    </ReportPage>
  );
};

export default CashMovements;
